import classNames from 'classnames'
import { HighlightText } from 'components/utils/search-highlight'
import React, { useCallback } from 'react'
import * as CommonConstants from '../constants'
import * as GlobalTypes from '../types'
import './styles.scss'
import { formatSourceType, getSourceDomain, getSourceTypeIcon } from './utils'

export interface SourceCardProps {
  source: GlobalTypes.Source
  isSelected: boolean
  onSelect: (source: GlobalTypes.Source) => void
  searchTerm?: string
  compact?: boolean
  showMetrics?: boolean
  disabled?: boolean
  onPreview?: (source: GlobalTypes.Source) => void
}

export const SourceCard: React.FC<SourceCardProps> = ({
  source,
  isSelected,
  onSelect,
  searchTerm = '',
  compact = false,
  showMetrics = false,
  disabled = false,
  onPreview,
}) => {
  const handleSelect = useCallback(() => {
    if (!disabled) {
      onSelect(source)
    }
  }, [source, onSelect, disabled])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
        e.preventDefault()
        onSelect(source)
      }
    },
    [source, onSelect, disabled]
  )

  const handlePreview = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onPreview && source.type === GlobalTypes.SourceTypes.rss) {
        onPreview(source)
      }
    },
    [source, onPreview]
  )

  const isRSSFeed = source.type === GlobalTypes.SourceTypes.rss
  const showPreviewButton = isRSSFeed && onPreview && !compact

  // Extract URL from source
  const url =
    source.type !== GlobalTypes.SourceTypes.facebook
      ? source.identifier
      : source.options
        ? JSON.parse(source.options).url
        : null

  // Get domain for display
  const domain = getSourceDomain(url || source.identifier)

  // Split categories for display
  const categories = source.category ? source.category.split(CommonConstants.CATEGORY_SPLIT_CHAR) : []

  const subcategories = source.sub_category ? source.sub_category.split(CommonConstants.CATEGORY_SPLIT_CHAR) : []

  // Combine categories and subcategories for tags
  const allTags = [...categories, ...subcategories].filter(Boolean)

  return (
    <div
      className={classNames('source-card', {
        'source-card--selected': isSelected,
        'source-card--compact': compact,
        'source-card--disabled': disabled,
        'source-card--interactive': !disabled,
      })}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
      role="button"
      aria-pressed={isSelected}
      aria-label={`${source.name} - ${formatSourceType(source.type)} source${isSelected ? ', selected' : ''}`}
    >
      {/* Header Section */}
      <div className="source-card__header">
        <div className="source-card__icon">{getSourceTypeIcon(source.type)}</div>

        <div className="source-card__title-section">
          <h3 className="source-card__name">
            <HighlightText text={source.name} searchTerm={searchTerm} className="search-highlight-animated" />
          </h3>
          <div className="source-card__metadata">
            <span className="source-card__type">{formatSourceType(source.type)}</span>
            {domain && (
              <>
                <span className="source-card__separator">•</span>
                <span className="source-card__domain">
                  <HighlightText text={domain} searchTerm={searchTerm} className="search-highlight-subtle" />
                </span>
              </>
            )}
          </div>
        </div>

        <div className="source-card__action-buttons">
          {showPreviewButton && (
            <button
              className="source-card__preview-button"
              onClick={handlePreview}
              aria-label="Preview RSS feed"
              title="Preview recent articles"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M1 3.5A1.5 1.5 0 0 1 2.5 2h11A1.5 1.5 0 0 1 15 3.5v8a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 1 11.5v-8zM2.5 3a.5.5 0 0 0-.5.5V5h12V3.5a.5.5 0 0 0-.5-.5h-11zM14 6H2v5.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 .5-.5V6z" />
                <circle cx="4" cy="8.5" r="0.5" />
                <path d="M6 8h6v1H6V8zM6 10h4v1H6v-1z" />
              </svg>
            </button>
          )}
          <button
            className={classNames('source-card__action-button', {
              'source-card__action-button--selected': isSelected,
            })}
            onClick={e => {
              e.stopPropagation()
              handleSelect()
            }}
            aria-label={isSelected ? 'Remove source' : 'Add source'}
            disabled={disabled}
          >
            {isSelected ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* URL Section (if different from domain and not compact) */}
      {!compact && url && url !== domain && (
        <div className="source-card__url">
          <HighlightText text={url} searchTerm={searchTerm} className="search-highlight-subtle" />
        </div>
      )}

      {/* Tags Section */}
      {allTags.length > 0 && (
        <div className="source-card__tags">
          {allTags.slice(0, compact ? 2 : 4).map((tag, index) => (
            <span key={index} className="source-card__tag">
              <HighlightText text={tag} searchTerm={searchTerm} className="search-highlight" />
            </span>
          ))}
          {allTags.length > (compact ? 2 : 4) && (
            <span className="source-card__tag source-card__tag--more">+{allTags.length - (compact ? 2 : 4)} more</span>
          )}
        </div>
      )}

      {/* Metrics Section (if enabled) */}
      {showMetrics && (
        <div className="source-card__metrics">
          <div className="source-card__metric">
            <span className="source-card__metric-icon">📊</span>
            <span className="source-card__metric-text">High engagement</span>
          </div>
          <div className="source-card__metric">
            <span className="source-card__metric-icon">🔄</span>
            <span className="source-card__metric-text">Updates daily</span>
          </div>
        </div>
      )}

      {/* Selection Indicator */}
      {isSelected && <div className="source-card__selection-indicator" />}
    </div>
  )
}
